import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Plus, Edit, Trash2, Loader2, Save, Layers, GripVertical } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

// `source` is fixed to "tags" since the unified-tags migration. The dropdown
// was removed from the UI but we still send `source: "tags"` on save so the
// backend payload remains valid for older versions.

const VENUE_TYPE_SLUGS = ["restaurant", "taverna", "bar", "cafe", "club"];

export default function AdminTagGroups() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [venueTypes, setVenueTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [grpRes, vtRes] = await Promise.all([
        axios.get(`${API}/admin/taxonomy/tag-groups`),
        axios.get(`${API}/taxonomy/venue-types`),
      ]);
      setGroups(grpRes.data || []);
      setVenueTypes(vtRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/taxonomy/tag-groups/${deleteTarget.group_id}`);
      toast.success(t("admin_tag_groups:toast_deleted", { defaultValue: "Tag group deleted" }));
      setDeleteTarget(null);
      fetchAll();
    } catch {
      toast.error(t("admin_tag_groups:toast_delete_failed", { defaultValue: "Failed to delete" }));
    } finally {
      setDeleting(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const reordered = Array.from(groups);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    const withOrder = reordered.map((g, i) => ({ ...g, sort_order: i }));
    setGroups(withOrder);
    try {
      await axios.post(`${API}/admin/taxonomy/tag-groups/reorder`, {
        ids: reordered.map((g) => g.group_id),
      });
      toast.success(t("admin_tag_groups:toast_reordered", { defaultValue: "Order saved" }));
    } catch {
      toast.error(t("admin_tag_groups:toast_reorder_failed", { defaultValue: "Failed to save order" }));
      fetchAll();
    }
  };

  return (
    <div data-testid="admin-tag-groups-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
            <Layers className="h-6 w-6 text-primary" />
            {t("admin_tag_groups:title", { defaultValue: "Tag Groups" })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("admin_tag_groups:subtitle", { defaultValue: "Editable headings for the chip rows on the public venue list. Each group is scoped to specific venue types." })}
          </p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="tag-groups-create-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t("admin_tag_groups:add", { defaultValue: "Add Group" })}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("admin_tag_groups:create_title", { defaultValue: "New Tag Group" })}</DialogTitle>
            </DialogHeader>
            <TagGroupForm
              venueTypes={venueTypes}
              onDone={() => {
                setShowCreate(false);
                fetchAll();
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-14 rounded-lg" />)}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="w-12">#</TableHead>
                <TableHead>{t("admin_tag_groups:col_heading", { defaultValue: "Heading (EN / EL)" })}</TableHead>
                <TableHead>{t("admin_tag_groups:col_slug", { defaultValue: "Slug" })}</TableHead>
                <TableHead>{t("admin_tag_groups:col_applies_to", { defaultValue: "Applies To" })}</TableHead>
                <TableHead className="text-right">{t("admin_venues:col_actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="tag-groups-droppable">
                {(provided) => (
                  <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                    {groups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                          {t("admin_tag_groups:empty", { defaultValue: "No tag groups yet — add one to start." })}
                        </TableCell>
                      </TableRow>
                    ) : groups.map((grp, index) => (
                      <Draggable key={grp.group_id} draggableId={grp.group_id} index={index}>
                        {(prov, snapshot) => (
                          <TableRow
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            data-testid={`tag-group-row-${grp.slug}`}
                            className={snapshot.isDragging ? "bg-muted shadow-lg" : ""}
                          >
                            <TableCell {...prov.dragHandleProps} className="cursor-grab text-muted-foreground hover:text-foreground" data-testid={`drag-handle-${grp.slug}`}>
                              <GripVertical className="h-4 w-4" />
                            </TableCell>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{grp.name}</p>
                                {grp.name_el && <p className="text-xs text-muted-foreground">{grp.name_el}</p>}
                              </div>
                            </TableCell>
                            <TableCell><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{grp.slug}</code></TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {(grp.applies_to && grp.applies_to.length)
                                  ? grp.applies_to.map((s) => (
                                    <span key={s} className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">{s}</span>
                                  ))
                                  : <span className="text-xs text-muted-foreground italic">{t("admin_taxonomy:applies_to_universal")}</span>}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setEditing(grp)} data-testid={`tag-group-edit-${grp.slug}`}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(grp)} data-testid={`tag-group-delete-${grp.slug}`}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("admin_tag_groups:edit_title", { defaultValue: "Edit Tag Group" })}</DialogTitle>
          </DialogHeader>
          {editing && (
            <TagGroupForm
              venueTypes={venueTypes}
              initial={editing}
              onDone={() => {
                setEditing(null);
                fetchAll();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-tag-group-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin_tag_groups:delete_title", { defaultValue: "Delete tag group?" })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin_tag_groups:delete_desc", { defaultValue: "Tags inside this group will be detached but not deleted. This action cannot be undone." })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("admin_venues:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-tag-group">
              {t("admin_venues:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TagGroupForm({ initial, venueTypes, onDone }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    name_el: initial?.name_el || "",
    slug: initial?.slug || "",
    source: initial?.source || "tags",
    applies_to: Array.isArray(initial?.applies_to) ? initial.applies_to : [],
    sort_order: initial?.sort_order ?? 0,
    is_active: initial?.is_active ?? true,
  }));
  const [saving, setSaving] = useState(false);

  const toggleApplies = (slug) => {
    setForm((f) => ({
      ...f,
      applies_to: f.applies_to.includes(slug)
        ? f.applies_to.filter((s) => s !== slug)
        : [...f.applies_to, slug],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial) {
        await axios.put(`${API}/admin/taxonomy/tag-groups/${initial.group_id}`, form);
        toast.success(t("admin_tag_groups:toast_updated", { defaultValue: "Tag group updated" }));
      } else {
        await axios.post(`${API}/admin/taxonomy/tag-groups`, form);
        toast.success(t("admin_tag_groups:toast_created", { defaultValue: "Tag group created" }));
      }
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="tag-group-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{t("admin_tag_groups:name_en", { defaultValue: "Heading (EN)" })} *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" />
        </div>
        <div>
          <Label>{t("admin_tag_groups:name_el", { defaultValue: "Heading (EL)" })}</Label>
          <Input value={form.name_el} onChange={(e) => setForm({ ...form, name_el: e.target.value })} className="mt-1" />
        </div>
      </div>
      <div>
        <Label>{t("admin_tag_groups:slug", { defaultValue: "Slug" })} *</Label>
        <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required className="mt-1" disabled={!!initial} />
      </div>
      <div>
        <Label>{t("admin_tag_groups:applies_to", { defaultValue: "Applies to venue types" })}</Label>
        <p className="text-xs text-muted-foreground mt-1 mb-2">
          {t("admin_taxonomy:applies_to_help")}
        </p>
        <div className="flex flex-wrap gap-2">
          {(venueTypes.length ? venueTypes : VENUE_TYPE_SLUGS.map(s => ({ id: s, slug: s, name: s }))).map((vt) => {
            const active = form.applies_to.includes(vt.slug);
            return (
              <button
                key={vt.slug}
                type="button"
                onClick={() => toggleApplies(vt.slug)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 text-foreground border-border hover:bg-muted"}`}
                data-testid={`tag-group-applies-${vt.slug}`}
              >
                {vt.name}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label>{t("admin_tag_groups:sort_order", { defaultValue: "Sort order" })}</Label>
        <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="mt-1" />
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-border">
        <Button type="submit" disabled={saving} data-testid="tag-group-save">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {initial ? t("admin_taxonomy:save") : t("admin_tag_groups:create", { defaultValue: "Create" })}
        </Button>
      </div>
    </form>
  );
}
